import { useTranslation } from "react-i18next";
import { tipoConocido, type CaracteristicasAgente } from "@/lib/api/modelosApi";

/**
 * Insignias que describen a un AGENTE: como esta construido y como se usa.
 *
 * POR QUE EXISTE. El panel de modelos dibuja la estructura real del sistema,
 * pero hasta ahora cada caja decia solo el nombre y el modelo. Con eso no se
 * distingue un agente con el que se conversa de uno que corre solo procesando
 * correos, ni uno que puede parar a pedir una aprobacion humana de uno que no,
 * ni un deep agent de un grafo de nodos escrito a mano. Son justo los datos con
 * los que se decide si a ese agente le conviene un modelo mas barato.
 *
 * UN SOLO COMPONENTE para el grafo y para la tabla. Son las dos vistas de lo
 * mismo, y la tabla es la ALTERNATIVA ACCESIBLE: si cada una compusiera sus
 * insignias, la accesible acabaria contando menos que la otra — que es
 * exactamente lo que una alternativa accesible no puede hacer.
 *
 * LA SIGLA NO SE ENSEÑA. "HITL" no le dice nada a quien no la conozca, y este
 * panel lo usa un admin, no un ingeniero de LangGraph. El texto visible va en
 * lenguaje de negocio ("Aprobación humana") y el detalle tecnico vive en el
 * `title`, donde lo encuentra quien lo busque sin estorbar a quien no.
 */

/** Una insignia. `tono` decide el color; `titulo` es la explicacion del hover. */
function Insignia({
  texto,
  titulo,
  tono,
  dato,
}: {
  texto: string;
  titulo: string;
  tono: "neutro" | "aviso";
  /** Valor de `data-insignia`, para que un test afirme CUAL se pinto. */
  dato: string;
}) {
  return (
    <span
      data-insignia={dato}
      title={titulo}
      className={`rounded px-1.5 py-0.5 text-[10px] leading-tight ${
        tono === "aviso"
          ? "bg-amber-500/15 text-amber-700"
          : "bg-[var(--muted)] text-[var(--muted-foreground)]"
      }`}
    >
      {texto}
    </span>
  );
}

export default function InsigniasAgente({
  caracteristicas,
  esSubagente = false,
  className = "",
}: {
  /**
   * `null`/ausente = NO es un agente (una operacion auxiliar) y no se pinta
   * nada. No es lo mismo que un agente con todo a `false`: una operacion no es
   * "no conversacional", es que la pregunta no aplica.
   */
  caracteristicas?: CaracteristicasAgente | null;
  /**
   * En un subagente el eje chat/autonomo NO aplica y no se pinta.
   *
   * El backend manda `conversacional: false` y es CIERTO —un subagente no esta
   * en el selector del chat—, pero pintar "Autonomo" diria que lo dispara un
   * proceso de fondo, y a un subagente lo invoca su agente principal con la
   * tool `task`. Es la misma clase de mentira que pintarle "deep agent" porque
   * su padre lo sea.
   */
  esSubagente?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!caracteristicas) return null;

  const tipo = tipoConocido(caracteristicas.tipo);

  return (
    <span className={`flex flex-wrap items-center gap-1 ${className}`}>
      {/* El TIPO. Si el backend manda uno que este front no conoce no se pinta
          insignia en vez de caer a "ReAct": una insignia ausente se nota, una
          equivocada no. */}
      {tipo && (
        <Insignia
          dato={`tipo-${tipo}`}
          texto={t(`admin.modelos.tipo.${tipo}`)}
          titulo={t(`admin.modelos.tipoAyuda.${tipo}`)}
          tono="neutro"
        />
      )}

      {/* Conversacional o autonomo. En un AGENTE esta siempre, en sus dos
          valores y no solo cuando es autonomo: es lo que mas cambia lo que se
          espera de el, y una insignia que solo aparece a veces se lee como una
          excepcion en vez de como un eje. En un SUBAGENTE no se pinta (ver
          `esSubagente`). */}
      {!esSubagente && (
      <Insignia
        dato={caracteristicas.conversacional ? "chat" : "autonomo"}
        texto={
          caracteristicas.conversacional
            ? t("admin.modelos.esConversacional")
            : t("admin.modelos.esAutonomo")
        }
        titulo={
          caracteristicas.conversacional
            ? t("admin.modelos.esConversacionalAyuda")
            : t("admin.modelos.esAutonomoAyuda")
        }
        tono="neutro"
      />
      )}

      {/* Las dos siguientes SOLO cuando son verdad: "no pide aprobacion" y "no
          exige adjunto" son el caso normal y ocupar sitio con ellos llenaria
          cada caja de ruido que no distingue nada. */}
      {caracteristicas.hitl && (
        <Insignia
          dato="hitl"
          texto={t("admin.modelos.tieneHitl")}
          titulo={t("admin.modelos.tieneHitlAyuda")}
          tono="aviso"
        />
      )}
      {caracteristicas.requires_document && (
        <Insignia
          dato="documento"
          texto={t("admin.modelos.exigeDocumento")}
          titulo={t("admin.modelos.exigeDocumentoAyuda")}
          tono="aviso"
        />
      )}
    </span>
  );
}
